import { useCallback } from 'react';

import { useAuth } from '@/components/auth/AuthProvider';
import useAnalytics from '@/hooks/useAnalytics';
import useGuestAddress from '@/hooks/useGuestAddress';

/** Where the download control was tapped, for analytics. */
export type BallotDownloadSource = 'header' | 'status_popover';

/**
 * The ballot download control for a guest with an address: the PDF export
 * renders an account's saved ballot, so the control opens sign in instead.
 * Mirrors the mobile app's BallotContent guest download.
 *
 * @returns the handler to pass as `onDownload`, or `undefined` when the voter
 *   is signed in or has no ballot to download.
 */
export default function useGuestBallotDownload():
  ((source: BallotDownloadSource) => void) | undefined {
  const { isAuthenticated, isInitializing, signIn } = useAuth();
  const analytics = useAnalytics();
  const { address } = useGuestAddress();
  // Wait for auth to settle so a returning voter never sees the guest variant.
  const enabled = !isInitializing && !isAuthenticated && Boolean(address?.formatted_address);

  const handleDownload = useCallback(
    (source: BallotDownloadSource) => {
      analytics.track('Ballot Download Sign In Clicked', { source });
      signIn();
    },
    [analytics, signIn]
  );

  return enabled ? handleDownload : undefined;
}
